/**
 * Sentry bootstrap — imported FIRST from `index.js` so it monkeypatches
 * the environment before React / fetch / any error can happen.
 *
 * If `REACT_APP_SENTRY_DSN` is unset, `Sentry.init` is skipped and the
 * exports become no-ops. Keeps local dev quiet and prevents a Sentry
 * outage from breaking the app.
 */
import * as Sentry from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const DSN = (process.env.REACT_APP_SENTRY_DSN || '').trim();
const ENV = (process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development').trim();
const RELEASE = (process.env.REACT_APP_SENTRY_RELEASE || '').trim() || undefined;
const TRACES_RATE = Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.0');

// --- Scrubber --------------------------------------------------------
// Removes Authorization headers, cookies, and any accidentally-set
// user.email before an event leaves the browser. We deliberately DON'T
// filter `event.request.data` here because CRA / React events almost
// never carry request bodies; if you start attaching them via Sentry
// contexts, revisit.
const scrub = (event) => {
  try {
    if (event.request) {
      if (event.request.cookies) delete event.request.cookies;
      const h = event.request.headers;
      if (h) {
        for (const k of Object.keys(h)) {
          if (/(authorization|cookie|token|password|secret|api[-_]?key|session)/i.test(k)) {
            h[k] = '[Filtered]';
          }
        }
      }
    }
    if (event.user && event.user.email) delete event.user.email;
  } catch { /* scrubber must never throw */ }
  return event;
};

if (DSN) {
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    // 100% of errors, sampled by count later if quota gets tight.
    sampleRate: 1.0,
    // Perf tracing off by default (user opted for errors-only). Bump
    // via env if you want spans without a redeploy.
    tracesSampleRate: Number.isFinite(TRACES_RATE) ? TRACES_RATE : 0.0,
    integrations: [
      // Auto-tag page-navigation transactions for react-router-v6.
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Console breadcrumbs off — noisy and rarely useful.
      Sentry.breadcrumbsIntegration({ console: false }),
    ],
    // Only trace calls to our own API — keeps span quota lean.
    tracePropagationTargets: [/^\/api\//, /pnt-preview\.preview\.emergentagent\.com\/api\//, /praynowtogether\.com\/api\//],
    beforeSend: scrub,
    // Ignore noisy junk we've seen in the wild:
    //   • Extensions that inject `Non-Error promise rejection captured`
    //   • Browser translation / content-script errors from `ResizeObserver`
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^Network Error$/,   // axios timeout / offline — expected, handled by app
      /Load failed/,       // Safari's terse offline message
    ],
  });
}

/**
 * Public helpers — components import these instead of `@sentry/react`
 * directly so we can swap the backend later without touching call sites.
 */
export function setSignedInUser(user) {
  try {
    if (user && user.user_id) Sentry.setUser({ id: String(user.user_id) });
    else Sentry.setUser(null);
  } catch { /* ignore */ }
}

export function clearSignedInUser() {
  try { Sentry.setUser(null); } catch { /* ignore */ }
}

/**
 * Capture a caught exception with optional metadata. Prefer this over
 * `Sentry.captureException` directly so we get a consistent scope tag
 * and PII-safe metadata surface.
 *
 * Example:
 *   try { await api.post(...) }
 *   catch (e) { reportError(e, { where: 'AddPrayer.publish', prayerId: id }); throw e; }
 */
export function reportError(err, meta = {}) {
  try {
    Sentry.withScope((scope) => {
      Object.entries(meta || {}).forEach(([k, v]) => {
        try {
          scope.setContext(k, typeof v === 'object' ? v : { value: String(v) });
        } catch { /* ignore */ }
      });
      Sentry.captureException(err);
    });
  } catch { /* ignore */ }
}

export { Sentry };
